import {
  ConnectedPosition,
  FlexibleConnectedPositionStrategy,
  Overlay,
  OverlayPositionBuilder,
  OverlayRef,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { PopoverComponent } from '@shared/components/popover/popover.component';
import { PopoverPosition, PopoverTrigger } from '@shared/components/popover/popover.types';

@Directive({
  selector: '[cxPopover]',
})
export class PopoverDirective implements OnDestroy {
  @Input({ required: true }) cxPopover!: string | TemplateRef<HTMLElement>;
  @Input() cxPopoverPosition: PopoverPosition = 'bottom-left';
  @Input() cxPopoverArrow = false;
  @Input() cxPopoverTrigger: PopoverTrigger = 'hover';

  private overlayRef?: OverlayRef;
  private strategy?: FlexibleConnectedPositionStrategy;

  constructor(
    private overlay: Overlay,
    private positionBuilder: OverlayPositionBuilder,
    private elementRef: ElementRef,
    private viewContainerRef: ViewContainerRef,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
  ) {}

  @HostListener('mouseenter')
  onMouseEnter() {
    if (this.cxPopoverTrigger === 'hover') {
      this.show();
    }
  }

  @HostListener('mouseleave')
  onMouseLeave() {
    if (this.cxPopoverTrigger === 'hover') {
      this.hide();
    }
  }

  @HostListener('click')
  onClick() {
    if (this.cxPopoverTrigger === 'click') {
      if (this.overlayRef) {
        this.hide();
      } else {
        this.show();
      }
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.cxPopoverTrigger === 'click' && this.overlayRef && !this.elementRef.nativeElement.contains(event.target)) {
      this.hide();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    this.hide();
  }

  show() {
    if (this.overlayRef) return;

    const positions = this.getPositionList();

    this.strategy = this.positionBuilder
      .flexibleConnectedTo(this.elementRef)
      .withPositions(positions)
      .withFlexibleDimensions(false)
      .withPush(false);

    this.overlayRef = this.overlay.create({
      positionStrategy: this.strategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
    });

    const portal = new ComponentPortal(PopoverComponent, this.viewContainerRef);
    const componentRef = this.overlayRef.attach(portal);

    this.strategy.positionChanges.subscribe(change => {
      const applied = change.connectionPair;
      const effectivePosition = this.mapConnectionToPosition(applied);

      this.ngZone.run(() => {
        componentRef.instance.position = effectivePosition;
        this.cdr.detectChanges();
      });
    });

    componentRef.instance.content = this.cxPopover;
    componentRef.instance.position = this.cxPopoverPosition;
    componentRef.instance.arrow = this.cxPopoverArrow;
  }

  hide() {
    const el = this.overlayRef?.overlayElement?.querySelector('.cx-popover');
    el?.classList?.add('fade-out');

    setTimeout(() => {
      this.overlayRef?.detach();
      this.overlayRef = undefined;
    }, 300);
  }

  ngOnDestroy() {
    this.overlayRef?.dispose();
  }

  private getPositionList(): ConnectedPosition[] {
    const isTop = this.cxPopoverPosition.startsWith('top');
    const originY = isTop ? 'top' : 'bottom';
    const overlayY = isTop ? 'bottom' : 'top';
    const offsetY = isTop ? -8 : 8;

    const preferredStart = this.cxPopoverPosition.endsWith('left');

    const positions: ConnectedPosition[] = [
      {
        originX: preferredStart ? 'start' : 'end',
        originY,
        overlayX: preferredStart ? 'start' : 'end',
        overlayY,
        offsetY,
      },
      {
        originX: preferredStart ? 'end' : 'start',
        originY,
        overlayX: preferredStart ? 'end' : 'start',
        overlayY,
        offsetY,
      },
    ];

    return positions;
  }

  private mapConnectionToPosition(pos: ConnectedPosition): PopoverPosition {
    const isTop = pos.overlayY === 'bottom';
    const isBottom = pos.overlayY === 'top';
    const isStart = pos.overlayX === 'start';

    if (isBottom) {
      return isStart ? 'bottom-left' : 'bottom-right';
    } else if (isTop) {
      return isStart ? 'top-left' : 'top-right';
    }
    return 'bottom-left';
  }
}
